import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    let retry;
    let closed = false;
    const connect = () => {
      const token = localStorage.getItem("pv_token");
      const url = API.replace(/^http/, "ws") + "/ws/notifications" + (token ? `?token=${encodeURIComponent(token)}` : "");
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.event === "message") {
          window.dispatchEvent(new CustomEvent("pv:message", { detail: msg.data }));
          toast(msg.data.from_name || "New message", { description: msg.data.text });
          return;
        }
        if (msg.event !== "notification") return;
        setItems((prev) => [msg.data, ...prev].slice(0, 50));
        setUnread((n) => n + 1);
        toast(msg.data.title, { description: msg.data.body });
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    const ping = setInterval(() => wsRef.current?.readyState === 1 && wsRef.current.send("ping"), 25000);
    return () => { closed = true; clearTimeout(retry); clearInterval(ping); wsRef.current?.close(); };
  }, [user?.user_id, load]);

  const markAllRead = async () => {
    await http.post("/notifications/read-all");
    setItems((p) => p.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const markRead = async (id) => {
    const target = items.find((n) => n.notification_id === id);
    if (!target || target.read) return;
    await http.post(`/notifications/${id}/read`);
    setItems((p) => p.map((n) => (n.notification_id === id ? { ...n, read: true } : n)));
    setUnread((n) => Math.max(0, n - 1));
  };

  return <Ctx.Provider value={{ items, unread, connected, markAllRead, markRead, reload: load }}>{children}</Ctx.Provider>;
}

export const useNotifications = () => useContext(Ctx);
